import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FolderIcon } from "@heroicons/react/outline";
import cosApi from "../../../../store/services/cos.services";
import silApi from "../../../../store/services/sil.services";
import CustomButton from "../../../common/Button/CustomButton";
import {
  executeMoveToFolder,
  MOVE_ALREADY_IN_DESTINATION,
} from "../../../../store/services/documentMove.service";

// Re-exported so existing imports from this module keep working. The move logic now
// lives in the shared documentMove service so it can be driven through the Redux saga.
export { executeMoveToFolder, MOVE_ALREADY_IN_DESTINATION };

const normalizeFolderList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
};

const MoveToParticipantFolderModal = ({
  open,
  onClose,
  participantUuid,
  workerUuid,
  silHomeUuid,
  item,
  itemType,
  bulkItems = null,
  moveDocumentRequest,
  onSuccess,
}) => {
  const isSilHome = Boolean(silHomeUuid);
  const isWorker = Boolean(workerUuid) && !isSilHome;
  const entityUuid = isSilHome ? silHomeUuid : workerUuid || participantUuid;

  const isBulk = Array.isArray(bulkItems) && bulkItems.length > 0;

  const [stack, setStack] = useState([{ id: null, uuid: null, name: "Documents" }]);
  const [childFolders, setChildFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const current = stack[stack.length - 1];
  const targetParentId = current?.id ?? null;

  useEffect(() => {
    if (open) {
      setStack([{ id: null, uuid: null, name: "Documents" }]);
    }
  }, [open, item?.id, item?.uuid, itemType]);

  useEffect(() => {
    if (!open || !entityUuid) {
      return;
    }
    let cancelled = false;
    setLoading(true);

    const fetchList = () => {
      if (isSilHome) {
        return silApi.getSilFolderListApi({
          uuid: silHomeUuid,
          folder_uuid: current?.uuid || undefined,
        });
      }
      const params = {
        uuid: entityUuid,
        folder_uuid: current?.uuid || undefined,
      };
      if (!isWorker) {
        params.role = "participant";
      }
      return cosApi.getFolderLIstForHrm(params);
    };

    fetchList()
      .then((res) => {
        if (cancelled) return;
        const body = res?.data ?? res;
        const folders = normalizeFolderList(body?.folder_structure);
        const excludeFolderIds = isBulk
          ? new Set(
              bulkItems
                .filter((b) => b?.itemType === "folder" && b?.item?.id != null)
                .map((b) => b.item.id)
            )
          : itemType === "folder" && item?.id != null
          ? new Set([item.id])
          : new Set();
        const filtered =
          excludeFolderIds.size > 0
            ? folders.filter((f) => f && !excludeFolderIds.has(f.id))
            : folders;
        setChildFolders(filtered);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(e?.message || "Could not load folders");
          setChildFolders([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    entityUuid,
    isSilHome,
    isWorker,
    silHomeUuid,
    current?.uuid,
    item?.id,
    itemType,
  ]);

  // Route a single move through the Redux move endpoint (saga) when available,
  // mirroring how bulk delete dispatches removeFileRequest/removeFolderRequest.
  // Falls back to a direct service call if the dispatcher was not provided.
  const runMove = (moveArgs) =>
    new Promise((resolve, reject) => {
      if (typeof moveDocumentRequest === "function") {
        moveDocumentRequest({
          ...moveArgs,
          skipToast: true,
          callback: (res, err) => (err ? reject(err) : resolve(res)),
        });
      } else {
        executeMoveToFolder({ ...moveArgs, silent: true }).then(resolve, reject);
      }
    });

  const handleBulkMove = async (destId = targetParentId) => {
    setSubmitting(true);
    let success = 0;
    let skipped = 0;
    let failed = 0;

    for (const entry of bulkItems) {
      try {
        await runMove({
          item: entry.item,
          itemType: entry.itemType,
          targetParentId: destId,
          workerUuid,
          silHomeUuid,
        });
        success += 1;
      } catch (e) {
        if (e?.code === MOVE_ALREADY_IN_DESTINATION) {
          skipped += 1;
        } else {
          failed += 1;
        }
      }
    }

    setSubmitting(false);

    if (success > 0) {
      toast.success(
        `${success} item${success !== 1 ? "s" : ""} moved successfully`
      );
    }
    if (skipped > 0) {
      toast.info(
        `${skipped} item${skipped !== 1 ? "s" : ""} already in this folder`
      );
    }
    if (failed > 0) {
      toast.error(`${failed} item${failed !== 1 ? "s" : ""} could not be moved`);
    }

    if (success > 0 && typeof onSuccess === "function") {
      onSuccess();
    }
    onClose();
  };

  const handleMove = async (destId = targetParentId) => {
    if (isBulk) {
      await handleBulkMove(destId);
      return;
    }
    if (!item) return;
    setSubmitting(true);
    try {
      await runMove({
        item,
        itemType,
        targetParentId: destId,
        workerUuid,
        silHomeUuid,
      });
      if (typeof onSuccess === "function") {
        onSuccess();
      }
      onClose();
    } catch (e) {
      if (e?.code === MOVE_ALREADY_IN_DESTINATION) {
        toast.info("This item is already in the selected folder.");
      } else {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          (typeof e === "string" ? e : "Move failed");
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const title = isBulk
    ? `Move ${bulkItems.length} item${bulkItems.length !== 1 ? "s" : ""}`
    : itemType === "folder"
    ? `Move folder "${item?.name || "Untitled"}"`
    : `Move file "${item?.description || item?.name || item?.label || "Untitled"}"`;

  return (
    <div className='fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-40 p-4'>
      <div
        className='bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col'
        role='dialog'
        aria-modal='true'
        aria-labelledby='move-folder-title'
      >
        <div className='px-5 py-4 border-b border-gray-200'>
          <h2 id='move-folder-title' className='text-lg font-semibold text-gray-900'>
            {title}
          </h2>
          <p className='text-sm text-gray-500 mt-1'>
            Click <span className='font-medium'>Move here</span> on a folder to move into it, or
            <span className='font-medium'> Open</span> to go deeper. Use
            <span className='font-medium'> ← Back</span> to return to the previous folder.
          </p>
        </div>

        <div className='px-5 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center gap-2 text-sm'>
          {stack.length > 1 && (
            <button
              type='button'
              className='inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 font-medium border border-gray-300 rounded px-2 py-0.5 mr-1 bg-white'
              onClick={() => setStack((s) => s.slice(0, -1))}
              disabled={submitting || loading}
              title='Go back one level'
            >
              ← Back
            </button>
          )}
          {stack.map((crumb, idx) => (
            <React.Fragment key={`${crumb.uuid || "root"}-${idx}`}>
              {idx > 0 && <span className='text-gray-400'>/</span>}
              <button
                type='button'
                className='text-blue-600 hover:underline font-medium'
                onClick={() => setStack((s) => s.slice(0, idx + 1))}
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        <div className='custom-scroll flex-1 min-h-0 max-h-[70vh] overflow-y-auto px-5 py-3'>
          {loading ? (
            <p className='text-sm text-gray-500'>Loading folders…</p>
          ) : childFolders.length === 0 ? (
            <div className='flex flex-col items-center justify-center text-center py-6 gap-3'>
              <p className='text-sm text-gray-500'>
                This folder has no subfolders. You can move the selected item
                {isBulk ? "s" : ""} here, or go back to choose another folder.
              </p>
              <div className='flex items-center gap-3'>
                {stack.length > 1 && (
                  <button
                    type='button'
                    className='text-xs font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded px-3 py-1.5 bg-white disabled:opacity-50'
                    onClick={() => setStack((s) => s.slice(0, -1))}
                    disabled={submitting || loading}
                  >
                    ← Back
                  </button>
                )}
                <button
                  type='button'
                  className='text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded px-3 py-1.5 disabled:opacity-50'
                  onClick={() => handleMove(targetParentId)}
                  disabled={submitting || loading}
                >
                  Move here
                </button>
              </div>
            </div>
          ) : (
            <ul className='divide-y divide-gray-100 border border-gray-200 rounded-md'>
              {childFolders.map((f) => (
                <li
                  key={f.uuid || f.id}
                  className='flex items-center justify-between px-3 py-2.5 hover:bg-blue-50'
                >
                  <button
                    type='button'
                    className='flex-1 min-w-0 text-left text-sm text-gray-800 flex items-center gap-2'
                    onClick={() =>
                      setStack((s) => [
                        ...s,
                        { id: f.id, uuid: f.uuid, name: f.name || "Folder" },
                      ])
                    }
                    title='Open folder'
                  >
                    <FolderIcon className='h-4 w-4 text-amber-500 flex-shrink-0' />
                    <span className='font-medium truncate'>{f.name || "Folder"}</span>
                  </button>
                  <div className='flex items-center gap-3 flex-shrink-0 ml-2'>
                    <button
                      type='button'
                      className='text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded px-2.5 py-1 disabled:opacity-50'
                      onClick={() => handleMove(f.id)}
                      disabled={submitting || loading}
                    >
                      Move here
                    </button>
                    <button
                      type='button'
                      className='text-xs text-blue-600 hover:underline'
                      onClick={() =>
                        setStack((s) => [
                          ...s,
                          { id: f.id, uuid: f.uuid, name: f.name || "Folder" },
                        ])
                      }
                    >
                      Open →
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className='px-5 py-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-lg'>
          <CustomButton
            type='button'
            label='Cancel'
            clickHandler={onClose}
            cssClass='bg-gray-200 text-gray-800 border-gray-300'
            variant='secondary'
            isDisabled={submitting}
          />
          <CustomButton
            type='button'
            label={
              submitting
                ? "Moving…"
                : `Move to "${current?.name || "Documents"}"`
            }
            clickHandler={() => handleMove(targetParentId)}
            variant='primary'
            isDisabled={submitting || loading}
          />
        </div>
      </div>
    </div>
  );
};

export default MoveToParticipantFolderModal;
